// Lead Quality Engine — client-side phone validation.
// Eliminates obviously fake/malformed numbers. NOT an ownership check (OTP is a future phase).

import { isBlockedPattern } from './blockedPatterns';

const INDIA = { iso2: 'in', nationalLength: 10, validPrefix: /^[6-9]/ };
const INTL_FALLBACK = { min: 6, max: 14 };

// Derive expected national-number lengths from the phone library's format mask.
const maskLengths = (format) => {
  if (!format) return [];
  const masks = typeof format === 'string' ? [format] : Object.values(format);
  const lengths = masks
    .map((m) => (typeof m === 'string' ? (m.match(/\./g) || []).length : 0))
    .filter((n) => n > 0);
  return [...new Set(lengths)];
};

/**
 * @param {string} e164 - phone in E.164 (e.g. "+919876543210")
 * @param {{iso2?: string, dialCode?: string, format?: string|object}} country
 * @returns {{valid: boolean, reason: 'passed'|'blocked_pattern'|'invalid_length'|'invalid_prefix'|'international_invalid'}}
 */
export const validateLeadPhone = (e164, country = {}) => {
  const iso2 = (country.iso2 || 'in').toLowerCase();
  const dial = String(country.dialCode || '');
  const digits = String(e164 || '').replace(/\D/g, '');
  const national = dial && digits.startsWith(dial) ? digits.slice(dial.length) : digits;

  if (!national) {
    return { valid: false, reason: iso2 === INDIA.iso2 ? 'invalid_length' : 'international_invalid' };
  }
  if (isBlockedPattern(national)) {
    return { valid: false, reason: 'blocked_pattern' };
  }

  if (iso2 === INDIA.iso2) {
    if (national.length !== INDIA.nationalLength) return { valid: false, reason: 'invalid_length' };
    if (!INDIA.validPrefix.test(national)) return { valid: false, reason: 'invalid_prefix' };
    return { valid: true, reason: 'passed' };
  }

  const expected = maskLengths(country.format);
  if (expected.length > 0) {
    const min = Math.max(Math.min(...expected) - 2, INTL_FALLBACK.min);
    const max = Math.max(...expected);
    if (national.length < min || national.length > max) {
      return { valid: false, reason: 'international_invalid' };
    }
  } else if (national.length < INTL_FALLBACK.min || national.length > INTL_FALLBACK.max) {
    return { valid: false, reason: 'international_invalid' };
  }
  return { valid: true, reason: 'passed' };
};
